/**
 * The Post sheet's routing contract (TECH-197 FE-4): which URLs draw the sheet, what a history
 * entry carries so the app knows what stands behind it, and what stands behind it on a cold start.
 * React-free, so the route shell, the cards that open the sheet and the scroll key share one rule.
 *
 * How it works: opening the sheet PUSHES its URL (`/questions/{slug}`, later `/live/{code}`) with
 * `state.postSheet.background`, the location the sheet is drawn over. `PostSheetOutlet` renders the
 * app's route table against that background, so the feed behind stays mounted, and draws the sheet
 * on top. Closing is always one history step back (`usePostSheetClose`), so web Back, Android Back
 * (`NativeBackButton` falls through to `history.back()`), Close, Escape and swipe-down all land on
 * the same entry, once. A cold start has no previous entry, so `QuestionRoute` writes one: it
 * replaces the cold entry with the background and pushes the sheet's entry over it.
 */
import { matchPath, type Location, type NavigateOptions, type Params } from 'react-router-dom';
import { featureFlags } from '@config/featureFlags';
import type { SpaceNestedRead } from '@models/api/event';
import type { LiveMe, LiveState } from '@models/api/livePoll';
import type { EventSummary, QuestionKind, SpaceSummary } from '@models/api/question';
import { getCommunityPath, getEventPath, isPathBuildable } from '@utils/slugHelpers';
import { routes } from '@routes';

/** How the sheet was reached: a tap in the app, or a link / refresh / push with nothing behind. */
export type PostSheetEntry = 'in-app' | 'cold-start';

/** The location the sheet is drawn over. Plain data: it lives in `history.state`. */
export interface PostSheetBackground {
  pathname: string;
  search: string;
  hash: string;
  state: unknown;
  key: string;
}

export interface PostSheetState {
  background: PostSheetBackground;
  entry: PostSheetEntry;
  /**
   * The opener did not know the post is a poll (it held only an id, e.g. a notification row with no
   * question context). The sheet body then hands anything its read does not confirm as a poll, a
   * post or a failed read, to the post page, so ordinary posts still get the post page.
   * Rides in `history.state` only when true.
   */
  unchecked: boolean;
  /**
   * The element that opened the sheet, as its `POST_SHEET_OPENER_ATTR` value (`postSheetOpenerMark`:
   * the post or row it opens plus the drawn element's own instance id): focus returns there when the
   * sheet closes (TECH-197 § The Post sheet). In `history.state`, so a reload of the sheet entry
   * still knows it. Null when the opener left no mark.
   */
  opener: string | null;
}

/** The attribute an opener carries so the sheet can hand focus back to it on close. */
export const POST_SHEET_OPENER_ATTR = 'data-post-sheet-opener';

/** Splits a mark into what it opens and which drawn element carries it. Never in a post id. */
const OPENER_INSTANCE_SEPARATOR = '#';

/**
 * An opener's mark: what it opens (a post id, a notification row id) and the instance that drew it
 * (React's `useId`, `usePostSheetOpener`). Per drawn element, not per post: the same post can be
 * drawn twice on one screen (a pinned copy and its feed copy), and focus goes back to the one tapped.
 */
export function postSheetOpenerMark(subject: string, instance: string): string {
  return `${subject}${OPENER_INSTANCE_SEPARATOR}${instance}`;
}

/** Spread on the element that opens the sheet: `<div {...postSheetOpenerProps(mark)}>`. */
export function postSheetOpenerProps(
  opener: string,
): Record<typeof POST_SHEET_OPENER_ATTR, string> {
  return { [POST_SHEET_OPENER_ATTR]: opener };
}

/**
 * The element marked `opener`, if it is still in the document. A mark from before a reload or a
 * remount names an instance that is gone: then the first element marked for the same subject.
 */
export function findPostSheetOpener(opener: string | null): HTMLElement | null {
  if (!opener || typeof document === 'undefined') return null;
  const marked = Array.from(document.querySelectorAll<HTMLElement>(`[${POST_SHEET_OPENER_ATTR}]`));
  const exact = marked.find(el => el.getAttribute(POST_SHEET_OPENER_ATTR) === opener);
  if (exact) return exact;
  const separator = opener.indexOf(OPENER_INSTANCE_SEPARATOR);
  if (separator < 0) return null;
  const subject = opener.slice(0, separator + 1);
  return marked.find(el => el.getAttribute(POST_SHEET_OPENER_ATTR)?.startsWith(subject)) ?? null;
}

/**
 * Every URL that can draw the Post sheet over a background. One entry per sheet body in
 * `src/components/PostSheet/postSheetRegistry.tsx`; `/live/:code` is the live poll's (TECH-867 FE-L3a).
 */
export const POST_SHEET_PATTERNS = ['/questions/:slugOrId', '/live/:code'] as const;
export type PostSheetPattern = (typeof POST_SHEET_PATTERNS)[number];

export interface PostSheetMatch {
  pattern: PostSheetPattern;
  params: Params;
}

export function matchPostSheetPath(pathname: string): PostSheetMatch | null {
  for (const pattern of POST_SHEET_PATTERNS) {
    const match = matchPath({ path: pattern, end: true }, pathname);
    if (match) return { pattern, params: match.params };
  }
  return null;
}

const isBackground = (value: unknown): value is PostSheetBackground => {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.pathname === 'string' &&
    v.pathname.startsWith('/') &&
    typeof v.search === 'string' &&
    typeof v.hash === 'string' &&
    typeof v.key === 'string'
  );
};

/**
 * The sheet this location draws, or null. Null unless the URL is a sheet URL AND its history
 * entry carries a background: a sheet URL without one is a cold start (or the post page), which
 * `QuestionRoute` handles. A background that is itself a sheet URL is refused (never a sheet
 * behind a sheet), so a malformed entry falls back to the cold-start path instead of recursing.
 */
export function readPostSheetState(
  location: Pick<Location, 'pathname' | 'state'>,
): PostSheetState | null {
  // Flag off, fully inert: a stale `state.postSheet` (an entry written while the flag was on, kept
  // by the browser across a release that turns it off) draws nothing.
  if (!featureFlags.postSheet) return null;
  if (!matchPostSheetPath(location.pathname)) return null;
  const state = location.state as { postSheet?: unknown } | null | undefined;
  const sheet = state?.postSheet as Partial<PostSheetState> | undefined;
  if (!sheet || !isBackground(sheet.background)) return null;
  if (matchPostSheetPath(sheet.background.pathname)) return null;
  return {
    background: sheet.background,
    entry: sheet.entry === 'cold-start' ? 'cold-start' : 'in-app',
    unchecked: sheet.unchecked === true,
    opener: typeof sheet.opener === 'string' ? sheet.opener : null,
  };
}

/**
 * The navigate options that open a sheet URL over `from` (the location the user is on). Opening a
 * sheet from inside a sheet keeps the first one's background, so the stack never nests.
 * `preventScrollReset` keeps the router from scrolling the feed behind to the top.
 */
export function postSheetNavigateOptions(
  // The location the user is on. `readLocation()` is typed URL-only, but what it returns at call
  // time is the router's whole Location, state and key included, and both are kept: the scroll key
  // of a sheet entry is its background's (`scrollRestorationKey`), which for a `scrollToTop` entry
  // is built from that entry's key; and a sheet open on `from` is detected and reused.
  from: PostSheetFrom,
  entry: PostSheetEntry = 'in-app',
  { unchecked = false, opener }: { unchecked?: boolean; opener?: string } = {},
): NavigateOptions {
  const fromState: unknown = from.state ?? null;
  const current = readPostSheetState({ pathname: from.pathname, state: fromState });
  const background: PostSheetBackground = current
    ? current.background
    : {
        pathname: from.pathname,
        search: from.search,
        hash: from.hash ?? '',
        state: fromState,
        key: from.key ?? 'post-sheet-background',
      };
  const postSheet = {
    background,
    entry,
    ...(unchecked ? { unchecked: true } : {}),
    ...(opener ? { opener } : {}),
  };
  return { state: { postSheet }, preventScrollReset: true };
}

/** Where an in-app open starts: the location the user is on (see `postSheetNavigateOptions`). */
export type PostSheetFrom = Pick<Location, 'pathname' | 'search'> &
  Partial<Pick<Location, 'hash' | 'state' | 'key'>>;

/**
 * An in-app open of a post URL from anywhere but a feed card (TECH-197 § Where a poll opens: "in-app,
 * the screen you came from"). With the flag on, a sheet URL opens over `from`, so Close lands back
 * on it; otherwise `navigate(path)` exactly as before.
 *
 * - `question` given (the caller holds the post): only a poll opens the sheet; any other post
 *   navigates as before, so ordinary posts are unchanged while the sheet is polls-only.
 * - `question` left out (the caller holds only an id): the sheet opens `unchecked`, showing its
 *   skeleton, and hands a post (or a failed read) to the post page once the read says what it is.
 * - `from` is itself a post URL with no sheet on it (the post page): never a background. Plain
 *   navigate; a poll then opens over its own background (QuestionRoute's cold-start rule).
 * - `opener`: the opening element's mark (`usePostSheetOpener`); focus returns to it on close.
 */
export function navigateToPost(
  navigate: (to: string, options?: NavigateOptions) => void,
  path: string,
  from: PostSheetFrom,
  question?: { kind?: QuestionKind | null },
  opener?: string,
): void {
  // Flag off: exactly as before, the path not even parsed.
  if (!featureFlags.postSheet) {
    navigate(path);
    return;
  }
  const pathname = new URL(path, 'https://app.invalid').pathname;
  const fromState: unknown = from.state ?? null;
  const opens =
    matchPostSheetPath(pathname) !== null &&
    (question === undefined || isPostSheetPost(question)) &&
    (!matchPostSheetPath(from.pathname) ||
      readPostSheetState({ pathname: from.pathname, state: fromState }) !== null);
  if (!opens) {
    navigate(path);
    return;
  }
  navigate(
    path,
    postSheetNavigateOptions(from, 'in-app', { unchecked: question === undefined, opener }),
  );
}

/** A background for a path the app computed (a cold start): no state of its own. */
export function backgroundFromPath(path: string): PostSheetBackground {
  const url = new URL(path, 'https://app.invalid');
  return {
    pathname: url.pathname,
    search: url.search,
    hash: url.hash,
    state: null,
    key: 'post-sheet-background',
  };
}

/**
 * Whether this post opens in the Post sheet. Polls only, behind `featureFlags.postSheet`; every
 * other post keeps the post page until the sheet is extended to them (TECH-197 § The Post sheet).
 */
export function isPostSheetPost(question: { kind?: QuestionKind | null }): boolean {
  return featureFlags.postSheet && question.kind === 'poll';
}

/**
 * What stands behind the sheet on a cold start (TECH-197 § Where a poll opens): "the first Space it
 * was posted to that the viewer can read (signed out: the first public listed one), an Event poll
 * over its Event page, else home".
 *
 * `space_summaries` is the backend's order (`space_questions.created_at NULLS LAST, id`) filtered by
 * `space_shown_to(viewer)` (CONTRACT § 9.1): active, and listed, or held as an APPROVED member or
 * created by the viewer. Shown is not readable: a listed PRIVATE Space reaches everyone, and its
 * page is a gate for a non-member. A summary says nothing about that; the read's nested `space`
 * (the same first Space, `build_question_space_read(visible_spaces[0])`) carries `is_private` and
 * `is_unlisted`, but not the viewer's membership (`user_is_member` is not enriched there). So:
 *   - public and listed: anyone reads it, signed out too;
 *   - unlisted: shown only to its approved members and creator, who read it (never to a guest);
 *   - private and listed, or a Space the read says nothing about (every Space after the first):
 *     not known to be readable, so not chosen (the Event page, else home, is the safer ground).
 * AIDEV-NOTE: a member of a private first Space is skipped too (no membership on the read); fix with a field.
 */
export function resolvePostSheetBackgroundPath(question: {
  space_summaries?: SpaceSummary[] | null;
  event_summaries?: EventSummary[] | null;
  /** The read's nested `space` (on the cache entity: `detail.space`). Required, so no caller drops it. */
  space: Pick<SpaceNestedRead, 'id' | 'is_private' | 'is_unlisted'> | null | undefined;
}): string {
  const first = question.space_summaries?.at(0);
  const nested = question.space;
  if (first && nested && first.id === nested.id && isPathBuildable(first)) {
    const publicListed = !nested.is_private && nested.is_unlisted === false;
    const unlistedMember = nested.is_unlisted === true;
    if (publicListed || unlistedMember) return getCommunityPath(first);
  }
  const event = question.event_summaries?.find(e => isPathBuildable(e));
  if (event) return getEventPath(event);
  return routes.home();
}

/** Where the post lives, as the Post sheet's header names it: its home Space, or its Event. */
export interface PostSheetPlace {
  name: string;
  /** The place's page (`/communities/{slug}`, `/events/{slug}`); null when it has none to open. */
  href: string | null;
  imageUrl: string | null;
}

/**
 * The post's home place for the sheet's header (TECH-197 § The Post sheet): its first Space, else
 * its first Event (an Event poll); the name opens that page.
 */
export function postSheetPlaceOf(post: {
  space_summaries?: SpaceSummary[] | null;
  event_summaries?: EventSummary[] | null;
}): PostSheetPlace | 'none' {
  const space = post.space_summaries?.at(0);
  if (space) {
    return {
      name: space.name,
      href: isPathBuildable(space) ? getCommunityPath(space) : null,
      imageUrl: space.image_url,
    };
  }
  const event = post.event_summaries?.at(0);
  if (event) {
    return {
      name: event.name,
      href: isPathBuildable(event) ? getEventPath(event) : null,
      imageUrl: event.image_url,
    };
  }
  return 'none';
}

/** What stands behind the live sheet: a page, or the neutral backdrop built from `/state`. */
export type LiveSheetBackground = { kind: 'page'; path: string } | { kind: 'neutral' };

/**
 * What stands behind `/live/{code}` (TECH-867 § Decided "Behind the sheet"; CONTRACT PR1, § 10 F5;
 * RESOLUTIONS R2): the join Space's page for someone who can open it (an approved member, `/me`'s
 * `join_space_slug`; anyone when it is listed and public, `/state`'s non-null slug), the Event page for an
 * Event with no Space, else the neutral backdrop (a non-member of an unlisted or private join Space: today's
 * Space page would answer its invite gate, and the live link is not a new way into it).
 */
export function resolveLiveSheetBackground(
  state: LiveState,
  me: LiveMe | null,
): LiveSheetBackground {
  if (me?.join_space_slug)
    return { kind: 'page', path: getCommunityPath({ id: null, slug: me.join_space_slug }) };
  if (state.join_space?.slug)
    return { kind: 'page', path: getCommunityPath({ id: null, slug: state.join_space.slug }) };
  // The place's own page only when there is no join Space: an Event poll whose Space is private or unlisted
  // (null slug) must not show its Event page to a non-member (FE-L3a review).
  if (state.join_space === null && state.place.slug) {
    const place = { id: null, slug: state.place.slug };
    return {
      kind: 'page',
      path: state.place.type === 'event' ? getEventPath(place) : getCommunityPath(place),
    };
  }
  return { kind: 'neutral' };
}

/** The live sheet header's link to its place: only a page the viewer can open (else the name alone). */
export function liveSheetPlaceHref(state: LiveState, me: LiveMe | null): string | null {
  const background = resolveLiveSheetBackground(state, me);
  return background.kind === 'page' ? background.path : null;
}
